import { useDispatch } from 'react-redux';
import { ContentPlanRootState as S } from '../../../elements/contentPlan/store';
import { Functions } from '../../../types/types';
import { ElementAction } from '../../../elements/elementAction';
import { NodeType } from '../../../enums/NodeType';
import { ViewType } from '../../../enums/ViewType';
import { ThunkDispatch } from 'redux-thunk';
import { getItemStatusId, getSchema } from '../../../utils';
import { Metadata } from '../../../types';
import { ItemStatus } from '../../../enums/ItemStatus';
import { SubType } from '../../../api/getResearchModels';
import { Tree } from '../../../enums/Tree';
import { ContentSubType } from '../../../enums/ContentSubType';

const getContextualizationChildren =
  (parentRefId: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { searchData } = getAPI();
    const schema = getSchema(
      getState().planning.selectedWorkspace.schemas,
      NodeType.Item
    );
    const fields = schema.props.fields.filter(f =>
      [
        'contentType',
        'contentSubtype',
        'contextType',
        'contextualizationOf'
      ].includes(f.field)
    );
    const resp = await searchData({
      filters: [
        {
          field: 'Item.contextualizationOf',
          conditions: [{ refIds: [parentRefId] }]
        }
      ],
      limit: 100,
      sortBy: [],
      view: {
        id: schema.id,
        labels: [],
        isMyItemsView: false,
        props: {
          data: schema.props.type,
          type: ViewType.Tabular
        },
        dataSchema: schema,
        card: {
          props: {
            data: schema.props.type,
            fields
          }
        }
      } as any
    });
    return resp?.data;
  };

const loadContextualizationConfig =
  (itemId: number): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const results = await getAPI().getContentTypeConfiguration(itemId);
    const supportedTypes = results?.data?.contextualization;
    if (!!supportedTypes) {
      return supportedTypes;
    }
    return null;
  };

const MQCCUpdateSubTypes = [ContentSubType.MQUpdate, ContentSubType.CCUpdate];

const loadSupportedBranchTypes =
  (
    data: Partial<Metadata>,
    supportedBranches: number[]
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const itemStatusId = getItemStatusId(data);
    const results = await getAPI().getDocumentSubtypes(
      184,
      (data?.props?.researchModel || [])[0]?.props?.id
    );
    if (!!results) {
      const supportedSubTypes = results.filter(st => {
        if (!st.isActive) return false;
        if (supportedBranches?.includes(st.id)) {
          if (
            MQCCUpdateSubTypes.includes(st.id) &&
            ![
              ItemStatus.Published,
              ItemStatus.PublishedHistoricalNonCPP
            ].includes(itemStatusId)
          ) {
            return false;
          }
          return true;
        }
        return false;
      });
      return supportedSubTypes;
    }
  };

const getSubtypeContextList =
  (subType: SubType): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (subType?.description?.toLocaleLowerCase()?.indexOf('context') != -1) {
      const results = await getAPI().getSubtypeContextList(subType?.id);
      if (!!results) {
        return results;
      }
    }
  };

const loadContextualizationContentSubType =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getTree } = getAPI();
    const ctTree = await getTree({ tree: Tree.ContentType });
    const ctToInclude = ['dtyp:281', 'dtyp:184'];
    return ctTree?.reduce<Metadata[]>((acc, ct) => {
      if (ctToInclude.includes(ct.props.refId)) {
        acc.push({ ...ct, metadata: undefined });
        if (ct.metadata?.length) {
          for (const st of ct.metadata) {
            acc.push(st);
          }
        }
      }
      return acc;
    }, []);
  };

export const useActions = () => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  return {
    loadContextualizationContentSubType: async () =>
      await dispatch(loadContextualizationContentSubType()),
    getContextualizationChildren: async (refId: string) =>
      dispatch(getContextualizationChildren(refId)),
    loadSupportedTypes: async (itemId: number) =>
      dispatch(loadContextualizationConfig(itemId)),
    loadSupportedBranchTypes: async (
      data: Partial<Metadata>,
      configuredBranches: number[]
    ) => dispatch(loadSupportedBranchTypes(data, configuredBranches)),
    getSubtypeContextList: async (subType: SubType) =>
      dispatch(getSubtypeContextList(subType))
  };
};
